/**
 * Member Volunteer — their own view only.
 *
 * There is deliberately no browsing list here: browsing happens through
 * events. This page answers "where do I stand, what have I committed to,
 * and what still needs help". SPEC.md §6.2.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { EXPECT_VOL, today, volunteerHours } from '../../data';
import { useClubData } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { DeptCard } from '../../components/DeptCard';
import { EventCard, HistoryRow } from '../../components/EventCard';
import {
  Empty,
  Eyebrow,
  Pill,
  SummaryCell,
  SummaryRow,
  ViewTitle,
  YearSplit,
} from '../../components/ui';

export function MemberVolunteerPage() {
  const { db, me, meId, leaveDepartment } = useClubData();
  const { toast } = useUI();
  const navigate = useNavigate();
  const now = today();
  const year = String(new Date().getFullYear());

  const myDeptIds = useMemo(
    () => db.signups.filter((s) => s.memberId === meId).map((s) => s.deptId),
    [db.signups, meId],
  );

  if (!me) return <Empty title="No member">Sign in again to see your volunteering.</Empty>;

  const hours = volunteerHours(db.volunteer, me.id, year);

  /** Departments they have signed up for on events still to come. */
  const upcomingDuty = db.departments.filter((d) => {
    if (!myDeptIds.includes(d.id)) return false;
    const event = db.events.find((e) => e.id === d.eventId);
    return !!event && event.date >= now;
  });

  /** Upcoming events that still have an unfilled department. */
  const needsHelp = db.events
    .filter((e) => e.date >= now)
    .filter((e) =>
      db.departments.some(
        (d) =>
          d.eventId === e.id && db.signups.filter((s) => s.deptId === d.id).length < d.needed,
      ),
    )
    .sort((a, b) => a.date.localeCompare(b.date));

  const log = db.volunteer
    .filter((v) => v.memberId === me.id)
    .sort((a, b) => b.date.localeCompare(a.date));
  const lifetime = log.filter((v) => v.verified).reduce((sum, v) => sum + v.hours, 0);

  // year ("2026") -> log entries in that year
  const byYear = {};
  for (const v of log) (byYear[v.date.slice(0, 4)] ??= []).push(v);
  const years = Object.keys(byYear).sort((a, b) => b.localeCompare(a));

  return (
    <>
      <ViewTitle
        title="My volunteering"
        sub={`The club expects ${EXPECT_VOL} hours a year — sign up from inside any event`}
      />

      <div className="border border-line border-t-[3px] border-t-sea-600 bg-white p-4">
        <div className="font-market text-[34px] font-extrabold leading-none text-sea-600">
          {hours}
          <small className="text-[15px] font-semibold text-copy-muted"> / {EXPECT_VOL} hrs</small>
        </div>
        <div className="mb-1.5 mt-3 h-2 overflow-hidden rounded-full bg-line-chip">
          <i
            className="block h-full rounded-full bg-sea-600 transition-[width] duration-500"
            style={{ width: `${Math.min(100, (hours / EXPECT_VOL) * 100)}%` }}
          />
        </div>
        <div className="font-market text-[10.5px] text-copy-label">
          {hours >= EXPECT_VOL
            ? 'Target met this year — thank you.'
            : `${(EXPECT_VOL - hours).toFixed(1)} hrs to go in ${year}.`}
        </div>
      </div>

      <div className="mt-3">
        <SummaryRow>
          {/* sea-600 — lifetime hours are the number this page celebrates */}
          <SummaryCell value={lifetime} label="Lifetime hours" color="#059669" />
          <SummaryCell value={log.filter((v) => v.verified).length} label="Duties completed" />
          <SummaryCell value={upcomingDuty.length} label="Upcoming duty" />
        </SummaryRow>
      </div>

      {upcomingDuty.length > 0 && (
        <>
          <Eyebrow>You&rsquo;re signed up for</Eyebrow>
          {upcomingDuty.map((d) => (
            <DeptCard
              key={d.id}
              dept={d}
              db={db}
              meId={meId}
              mode="member"
              onLeave={async (id) => {
                await leaveDepartment(id);
                toast('Sign-up cancelled');
              }}
            />
          ))}
        </>
      )}

      <Eyebrow>Events needing volunteers</Eyebrow>
      {needsHelp.length ? (
        <>
          <div className="mb-2.5 border border-line border-l-[3px] border-l-sea-600 bg-white px-4 py-3.5">
            <div className="text-[13.5px] leading-relaxed text-copy-muted">
              Open an event below and use <b>Help run this event</b> to pick a department.
            </div>
          </div>
          {needsHelp.map((e) => (
            <EventCard
              key={e.id}
              event={e}
              db={db}
              meId={meId}
              onOpen={(id) => navigate(`/m/events/${id}`)}
            />
          ))}
        </>
      ) : (
        <Empty padding={26}>Nothing needs volunteers right now.</Empty>
      )}

      <Eyebrow>Your volunteering history</Eyebrow>
      {log.length ? (
        years.map((y) => {
          const verified = byYear[y].filter((v) => v.verified).reduce((s, v) => s + v.hours, 0);
          return (
            <div key={y}>
              <YearSplit>
                {y} · {verified} hrs
              </YearSplit>
              {byYear[y].map((v) => {
                const event = v.eventId ? db.events.find((e) => e.id === v.eventId) : null;
                return (
                  <HistoryRow
                    key={v.id}
                    date={v.date}
                    title={v.role}
                    meta={`${event ? event.title : 'Club duty'} · ${v.hours} hrs`}
                    tag={
                      v.verified ? (
                        <Pill tone="ok">Verified</Pill>
                      ) : (
                        <Pill tone="soon">Pending</Pill>
                      )
                    }
                  />
                );
              })}
            </div>
          );
        })
      ) : (
        <Empty title="No duty yet" padding={34}>
          Sign up for a department above — your hours will be listed here.
        </Empty>
      )}
    </>
  );
}
